import { get, post, patch, del } from "./index";
import http from "./http";

// types are kept simple, adjust as backend evolves
export interface TeamCheckin {
  id: string;
  title: string;
  checkinTime: string; // ISO string
  created_by: string;
  is_public: boolean;
  roomId: string;
}

export interface TeamNote {
  id: string;
  note: string;
  createdAt: string; // ISO string
  created_by: string;
  is_public: boolean;
  roomId: string;
}

const teamModeApi = {
  // room endpoints
  generateRoomId: async (): Promise<{ roomId: string }> => {
    return get("/focus/team/room-id");
  },
  createSession: async (data: any): Promise<any> => {
    const resp = await post("/focus/team", data);
    return resp;
  },
  getSession: async (id: string): Promise<any> => {
    return get(`/focus/team/${id}`);
  },

  // checkin endpoints
  // backend currently returns { total, checkins: TeamCheckin[] }
  listCheckins: async (
    roomId: string,
    params?: { limit?: number; offset?: number; is_public?: boolean },
  ): Promise<{ total?: number; checkins: TeamCheckin[] } | TeamCheckin[]> => {
    const qs = params
      ? "?" +
      new URLSearchParams(
        Object.entries(params as Record<string, any>).reduce(
          (acc, [k, v]) => {
            if (v !== undefined && v !== null) acc[k] = String(v);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString()
      : "";
    return get(`/focus/team/${roomId}/checkins${qs}`);
  },

  createCheckin: async (
    roomId: string,
    data: Partial<Omit<TeamCheckin, "id" | "created_by" | "roomId">>,
  ): Promise<TeamCheckin> => {
    return post(`/focus/team/${roomId}/checkins`, data);
  },

  updateCheckin: async (
    roomId: string,
    checkinId: string,
    data: Partial<Omit<TeamCheckin, "id" | "created_by" | "roomId">>,
  ): Promise<TeamCheckin> => {
    return patch(`/focus/team/${roomId}/checkins/${checkinId}`, data);
  },

  deleteCheckin: async (roomId: string, checkinId: string): Promise<void> => {
    return del(`/focus/team/${roomId}/checkins/${checkinId}`);
  },

  // notes endpoints
  listNotes: async (
    roomId: string,
    params?: { limit?: number; offset?: number; is_public?: boolean },
  ): Promise<TeamNote[]> => {
    const qs = params
      ? "?" +
      new URLSearchParams(
        Object.entries(params as Record<string, any>).reduce(
          (acc, [k, v]) => {
            if (v !== undefined && v !== null) acc[k] = String(v);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString()
      : "";
    return get(`/focus/team/${roomId}/notes${qs}`);
  },

  createNote: async (
    roomId: string,
    data: Partial<Omit<TeamNote, "id" | "created_by" | "roomId" | "createdAt">>,
  ): Promise<TeamNote> => {
    return post(`/focus/team/${roomId}/notes`, data);
  },

  updateNote: async (
    roomId: string,
    noteId: string,
    data: Partial<Omit<TeamNote, "id" | "created_by" | "roomId" | "createdAt">>,
  ): Promise<TeamNote> => {
    return patch(`/focus/team/${roomId}/notes/${noteId}`, data);
  },

  deleteNote: async (roomId: string, noteId: string): Promise<void> => {
    return del(`/focus/team/${roomId}/notes/${noteId}`);
  },

  // tasks endpoints
  listTasks: async (
    roomId: string,
    params?: { limit?: number; offset?: number; is_public?: boolean },
  ): Promise<any[]> => {
    const qs = params
      ? "?" +
      new URLSearchParams(
        Object.entries(params as Record<string, any>).reduce(
          (acc, [k, v]) => {
            if (v !== undefined && v !== null) acc[k] = String(v);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString()
      : "";
    return get(`/focus/team/${roomId}/tasks${qs}`);
  },

  createTask: async (
    roomId: string,
    data: { title: string; description?: string; status?: string; is_public?: boolean },
  ): Promise<any> => {
    return post(`/focus/team/${roomId}/tasks`, data);
  },

  updateTask: async (
    roomId: string,
    taskId: string,
    data: { title?: string; description?: string; status?: string; is_public?: boolean },
  ): Promise<any> => {
    return patch(`/focus/team/${roomId}/tasks/${taskId}`, data);
  },

  deleteTask: async (roomId: string, taskId: string): Promise<void> => {
    return del(`/focus/team/${roomId}/tasks/${taskId}`);
  },

  // RTC token endpoint
  getRtcToken: async (teamId: string): Promise<{ rtcToken: string }> => {
    return post(`/focus/team/${teamId}/rtc-token`);
  },
};

export default teamModeApi;
